import React from 'react';
import { Box, Stack, Tooltip, Typography, useTheme, alpha } from '@mui/material';
import type { HourlyObjectBucket } from '../../utils/creationsByHour';

type Props = {
  data: HourlyObjectBucket[];
  periodLabel: string;
};

const CreationsByHourChart: React.FC<Props> = ({ data, periodLabel }) => {
  const theme = useTheme();
  const foundColor = theme.palette.success.main;
  const claimedColor = theme.palette.primary.main;
  const deliveryColor = theme.palette.warning.main;
  const maxCount =
    data.reduce(
      (max, bucket) => Math.max(max, bucket.foundCount, bucket.claimedCount, bucket.deliveryCount),
      0,
    ) || 1;
  const totalFound = data.reduce((sum, bucket) => sum + bucket.foundCount, 0);
  const totalClaimed = data.reduce((sum, bucket) => sum + bucket.claimedCount, 0);
  const totalDelivery = data.reduce((sum, bucket) => sum + bucket.deliveryCount, 0);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 2, gap: 2, flexWrap: 'wrap' }}
      >
        <Typography variant="body2" color="text.secondary">
          {totalFound} trouvé{totalFound > 1 ? 's' : ''} · {totalClaimed} restitué
          {totalClaimed > 1 ? 's' : ''} · {totalDelivery} en livraison — {periodLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Heure locale du navigateur
        </Typography>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 12, height: 12, borderRadius: 0.75, bgcolor: foundColor }} />
          <Typography variant="caption" color="text.secondary">
            Objets trouvés
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 12, height: 12, borderRadius: 0.75, bgcolor: claimedColor }} />
          <Typography variant="caption" color="text.secondary">
            Objets restitués
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 12, height: 12, borderRadius: 0.75, bgcolor: deliveryColor }} />
          <Typography variant="caption" color="text.secondary">
            Partis en livraison
          </Typography>
        </Stack>
      </Stack>

      {totalFound === 0 && totalClaimed === 0 && totalDelivery === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Aucune activité sur la période sélectionnée.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(24, minmax(0, 1fr))',
            gap: { xs: 0.35, sm: 0.6 },
            alignItems: 'end',
            height: { xs: 180, sm: 220 },
            px: { xs: 0.25, sm: 0 },
          }}
        >
          {data.map((bucket) => (
            <Tooltip
              key={bucket.hour}
              title={`${bucket.label} : ${bucket.foundCount} trouvé${bucket.foundCount > 1 ? 's' : ''}, ${bucket.claimedCount} restitué${bucket.claimedCount > 1 ? 's' : ''}, ${bucket.deliveryCount} en livraison`}
              arrow
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', minWidth: 0 }}>
                <Box
                  sx={{
                    flex: 1,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    gap: '1px',
                  }}
                >
                  {[
                    { count: bucket.foundCount, color: foundColor },
                    { count: bucket.claimedCount, color: claimedColor },
                    { count: bucket.deliveryCount, color: deliveryColor },
                  ].map((bar, index) => {
                    const heightPct = Math.max(4, (bar.count / maxCount) * 100);
                    return (
                      <Box
                        key={index}
                        sx={{
                          width: '32%',
                          maxWidth: 11,
                          height: `${heightPct}%`,
                          minHeight: bar.count > 0 ? 6 : 2,
                          borderRadius: '6px 6px 2px 2px',
                          bgcolor: bar.count > 0 ? bar.color : alpha(bar.color, 0.15),
                          transition: 'height 0.25s ease, background-color 0.2s ease',
                        }}
                      />
                    );
                  })}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mt: 0.75,
                    fontSize: { xs: '0.58rem', sm: '0.68rem' },
                    lineHeight: 1,
                    display: { xs: bucket.hour % 3 === 0 ? 'block' : 'none', sm: 'block' },
                  }}
                >
                  {bucket.label}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CreationsByHourChart;
